import React from 'react';
import { gql, useApolloClient, useQuery } from '@apollo/client';
import { AsyncSelect } from '../ReactSelect';
import { getNameLikeCondition } from '../../helpers/search';
import { SourceType } from '../WaterService/SourceTypePicker';
import { WaterSiteType } from './WaterSiteTypePicker';

export const FETCH_WATER_SITES = gql`
  query GetPickerWaterSites($where: JSON, $orderBy: [ColumnOrder]) {
    waterSites(where: $where, limit: null, orderBy: $orderBy) {
      id
      name
      siteNumber
      address {
        id
        street1
        street2
        city
        state
        zip
      }
    }
  }
`;

export type WaterSite = {
  id: string;
  name: string;
  siteNumber?: string;
  address: {
    id: string;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
  };
};

type WaterSitePickerProps = {
  waterSiteId?: string | null;
  siteType: WaterSiteType;
  // where?: any;
  onChange?: (waterSite: WaterSite | null) => void;
  placeholder?: string;
  isClearable?: boolean;
  autoFocus?: boolean;
};

const WaterSitePicker = ({
  waterSiteId,
  siteType,
  // where,
  onChange,
  placeholder,
  isClearable,
  autoFocus,
}: WaterSitePickerProps): JSX.Element => {
  // where = where ?? {};
  const client = useApolloClient();

  const { error: itemError, data: itemData } = useQuery<{
    waterSites: WaterSite[];
  }>(FETCH_WATER_SITES, {
    variables: {
      where: {
        id: waterSiteId,
      },
    },
    skip: !waterSiteId,
  });

  const selected: WaterSite | undefined = itemData?.waterSites[0];

  if (itemError) {
    throw itemError;
  }

  const loadOptionsFunc = async (inputValue: string) => {
    const inputMatch = inputValue
      ? {
          or: [
            {
              name: getNameLikeCondition(inputValue),
            },
            {
              siteNumber: {
                startsWith: inputValue,
              },
            },
            {
              ['address.street1']: getNameLikeCondition(inputValue),
            },
          ],
        }
      : undefined;

    const res = await client.query<{ waterSites: WaterSite[] }>({
      query: FETCH_WATER_SITES,
      fetchPolicy: 'network-only',
      variables: {
        where: {
          type: siteType,
          ...inputMatch,
        },
        limit: null,
        orderBy: inputValue ? undefined : [{ col: 'name', dir: 'ASC' }],
      },
    });

    if (res.data && res.data.waterSites) {
      if (!selected && res.data.waterSites.length === 1) {
        onChange?.(res.data.waterSites[0]);
      }

      return res.data.waterSites;
    }

    return [];
  };

  return (
    <AsyncSelect
      autoFocus={autoFocus}
      cacheOptions
      value={selected}
      loadOptions={loadOptionsFunc}
      defaultOptions
      isClearable={isClearable}
      noOptionsMessage={() => null}
      menuPlacement="auto"
      styles={{
        menu: (base) => ({
          ...base,
          minWidth: '300px',
        }),
      }}
      onChange={(selected: WaterSite | null) => {
        if (!selected) {
          onChange?.(null);
        } else {
          onChange?.(selected);
        }
      }}
      placeholder={placeholder ?? 'Site name, number, street...'}
      getOptionLabel={(waterSite) =>
        waterSite.name +
        (waterSite.siteNumber ? ` (${waterSite.siteNumber})` : '')
      }
      getOptionValue={(waterSite) => waterSite.id}
    />
  );
};

export default WaterSitePicker;
